import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";

import { roleProviderId } from "@smart/bridge-smokeball-basic";
import { useAsync } from "@smart/itops-hooks-dom";

import {
  buildChangedMappedFields,
  getGroupMappedFieldNames,
  hasMappedFieldSelectionChanged,
} from "./helpers";
import { PickerSelection } from "./types";
import { GqlField, GqlGroup, GqlMatterType } from "../../../types";
import { LoadMatterFields, MatterField } from "../../types";
import { isContactDetailsGroup as checkIsContactDetailsGroup } from "../../utils";

const filterIndexMatterFields = (
  matterFields: Awaited<ReturnType<LoadMatterFields>>,
) => {
  const indexRegEx = /\[\d+\]$/;
  const nameSet = new Set();
  const output = [];

  for (const matterField of matterFields) {
    const noIndexName = matterField.name.replace(indexRegEx, "").trim();
    if (!nameSet.has(noIndexName)) {
      nameSet.add(noIndexName);
      output.push({ ...matterField, name: noIndexName });
    }
  }

  return output;
};

export const useMappedMatterFields = ({
  selection,
  matterTypes,
  fields,
  groups,
  destinationGroup,
  loadMatterFields,
}: {
  selection: PickerSelection;
  matterTypes?: GqlMatterType[];
  groups: GqlGroup[];
  fields: GqlField[];
  destinationGroup?: GqlGroup;
  loadMatterFields: LoadMatterFields;
}) => {
  const [isLoading, setIsLoading] = useState<boolean>();
  const [matterFields, setMatterFields] = useState<MatterField[]>([]);
  const currentLayoutComponent = [...selection.components]
    .filter((c) => c.type !== "path")
    .reverse()[0];
  const isContactDetailsDestination = checkIsContactDetailsGroup(
    destinationGroup?.values,
  );

  const mapSharedPrefixFields = (
    inputFields: MatterField[],
    prefix: string,
  ): MatterField[] =>
    inputFields
      .filter((f) => f.name.startsWith(prefix))
      .map((f) => {
        const parts = f.name
          .replace(new RegExp(`^${prefix}/`), "")
          .split("/")
          .filter(Boolean);
        const displayName = parts[parts.length - 1];
        const details = parts.slice(0, -1).join("/");
        return {
          ...f,
          displayName,
          details,
        };
      });

  const valueOrDefault = <T>(
    value: T | null | undefined,
    defaultValue?: T | null,
  ) => value || defaultValue;

  useAsync(
    async (info) => {
      setIsLoading(undefined);
      if (!selection.layout || !selection.components.length) {
        setMatterFields([]);
        return;
      }
      setIsLoading(true);

      try {
        const loaded = filterIndexMatterFields(
          await loadMatterFields({
            matterLayoutId: selection.layout.id,
            matterTypeIds: matterTypes?.map((mt) => mt.id),
            providerId: selection.layout.providerId,
          }),
        );

        const contactTypeRoleNames = ["person", "company", "trust"];
        const isContactTypeRoleFolder =
          currentLayoutComponent.type === "role" &&
          contactTypeRoleNames.includes(currentLayoutComponent.name);

        const loadedRoleFields =
          currentLayoutComponent.type === "role" && !isContactTypeRoleFolder
            ? await loadMatterFields({
                matterLayoutId: selection.layout.id,
                matterTypeIds: matterTypes?.map((mt) => mt.id),
                providerId: roleProviderId,
              })
            : [];

        const loadedLayoutFields =
          currentLayoutComponent.type === "role"
            ? mapSharedPrefixFields(
                loaded.filter((f) => f.type !== "Role"),
                currentLayoutComponent.name,
              )
            : loaded;

        if (info.mounted) {
          setMatterFields([...loadedLayoutFields, ...loadedRoleFields]);
          setIsLoading(false);
        }
      } catch {
        setIsLoading(false);
      }
    },
    [selection.layout?.id, matterTypes, selection.components],
  );

  const existingMappedFields = useMemo(() => {
    const selectionRole = selection.components.find((c) => c.type === "role");
    const isCompanyContactSelected = selectionRole?.name === "company/contact";
    const isExistingCompanyContactField = (
      fieldGroup: GqlGroup | undefined,
    ) => {
      if (!fieldGroup || fieldGroup.values.field?.name !== "company/contact")
        return false;
      return fieldGroup.values.layout?.id === selection.layout?.id;
    };

    return fields.reduce((agg, field) => {
      const group = groups.find((g) => g.uri === field.values.groupUri);
      const isContactDetailsFieldGroup = checkIsContactDetailsGroup(
        group?.values,
      );

      if (group?.values.type === "layoutContact" && group.values.field) {
        if (
          !selectionRole ||
          !selection.layout?.id ||
          !group.values.layout ||
          valueOrDefault(group.values.layout.parentId) !==
            valueOrDefault(selection.layout?.parentId) ||
          group.values.layout.id !== selection.layout?.id ||
          group.values.field.name !== selectionRole.name ||
          !field.values.field
        )
          return agg;
      } else if (
        // contactDetails group.field is the contact type (person/company), not a
        // picker role. Match those fields like ungrouped mapped fields instead.
        (!isContactDetailsFieldGroup &&
          group?.values.field &&
          valueOrDefault(selectionRole?.name) !==
            valueOrDefault(group.values.field.name)) ||
        // ungrouped field on the role still collides with a contact card
        (selectionRole && !group && !isContactDetailsDestination) ||
        !selection.layout?.id ||
        !field.values.layout ||
        valueOrDefault(field.values.layout.parentId) !==
          valueOrDefault(selection.layout?.parentId) ||
        field.values.layout.id !== selection.layout?.id ||
        !field.values.field ||
        (isCompanyContactSelected && !isExistingCompanyContactField(group))
      )
        return agg;

      return [...agg, field.values.field.name];
    }, [] as string[]);
  }, [
    selection.layout?.id,
    selection.components,
    fields,
    groups,
    isContactDetailsDestination,
  ]);

  return {
    currentLayoutComponent,
    isLoading,
    matterFields,
    existingMappedFields,
  };
};

export const useContactDetailsMappedFields = ({
  destinationGroup,
  fields,
  matterFields,
  isLoadingMatterFields,
  selection,
  setSelection,
}: {
  destinationGroup?: GqlGroup;
  fields: GqlField[];
  matterFields: MatterField[];
  isLoadingMatterFields: boolean | undefined;
  selection: PickerSelection;
  setSelection: Dispatch<SetStateAction<PickerSelection>>;
}) => {
  const [hasSeededExistingFields, setHasSeededExistingFields] = useState(false);
  const isContactDetailsGroup = checkIsContactDetailsGroup(
    destinationGroup?.values,
  );

  const groupFields = useMemo(
    () =>
      fields.filter((field) => field.values.groupUri === destinationGroup?.uri),
    [fields, destinationGroup?.uri],
  );

  const groupMappedFieldNames = useMemo(() => {
    if (!isContactDetailsGroup) return [];

    const namesInGroup = getGroupMappedFieldNames(
      groupFields,
      destinationGroup?.uri,
    );
    const loadedMatterFieldNames = new Set(
      matterFields.map((field) => field.name),
    );
    return namesInGroup.filter((name) => loadedMatterFieldNames.has(name));
  }, [groupFields, destinationGroup?.uri, isContactDetailsGroup, matterFields]);

  useEffect(() => {
    setHasSeededExistingFields(false);
  }, [destinationGroup?.uri]);

  useEffect(() => {
    if (
      !isContactDetailsGroup ||
      isLoadingMatterFields !== false ||
      hasSeededExistingFields
    ) {
      return;
    }

    setSelection((prev) => ({
      ...prev,
      fields: matterFields.filter((field) =>
        groupMappedFieldNames.includes(field.name),
      ),
    }));
    setHasSeededExistingFields(true);
  }, [
    isContactDetailsGroup,
    isLoadingMatterFields,
    hasSeededExistingFields,
    matterFields,
    groupMappedFieldNames,
    setSelection,
  ]);

  const { fieldsToAdd, fieldUrisToRemove } = isContactDetailsGroup
    ? buildChangedMappedFields({
        selectedFields: selection.fields,
        existingFieldNames: groupMappedFieldNames,
        groupFields,
      })
    : { fieldsToAdd: selection.fields, fieldUrisToRemove: [] };

  return {
    groupMappedFieldNames,
    fieldsToAdd,
    fieldUrisToRemove,
    hasSelectionChanged: isContactDetailsGroup
      ? hasSeededExistingFields &&
        hasMappedFieldSelectionChanged({
          selectedFieldNames: selection.fields.map((field) => field.name),
          existingFieldNames: groupMappedFieldNames,
        })
      : selection.fields.length > 0,
  };
};
